import React, {useRef, useEffect, useState} from 'react';
import * as d3 from 'd3';

// Example props: data = [{ month: "Jan'25", values: [{key, value, text}, ...] }, ...]
// exitData = [{ month: "Jan'25", exits: 10 }, ...]
const D3GroupedBarChart = ({data, exitData, height = 300}) => {
    const containerRef = useRef();
    const svgRef = useRef();
    const tooltipRef = useRef();
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setWidth(containerRef.current.offsetWidth);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useEffect(() => {
        if (!width) return;
        d3.select(svgRef.current).selectAll('*').remove();

        // Margins
        const margin = {top: 40, right: 70, bottom: 50, left: 70};

        // Preprocess: Filter invalid/non-positive values from each month's data,
        // but ensure all months from the input 'data' prop are included in 'processed'
        // so they appear on the X-axis.
        const processed = data.map((d) => {
            // Ensure d.values is an array before filtering; provide default empty array if undefined/null
            const valuesArray = Array.isArray(d.values) ? d.values : [];
            const validValues = valuesArray.filter((v) => typeof v.value === 'number' && !isNaN(v.value) && v.value > 0);
            return {month: d.month, values: validValues};
        });

        const months = processed.map((d) => d.month);

        // X0: months
        const x0 = d3
            .scaleBand()
            .domain(months)
            .range([margin.left, width - margin.right])
            .paddingInner(0.2);

        // X1: values within each month
        const maxValuesPerMonth = d3.max(processed, (d) => d.values.length);
        const x1 = d3.scaleBand().domain(d3.range(maxValuesPerMonth)).range([0, x0.bandwidth()]).padding(0.1);

        // Y: percent for bars
        const y = d3
            .scaleLinear()
            .domain([0, d3.max(processed, (d) => d3.max(d.values, (v) => v.value)) || 0])
            .nice()
            .range([height - margin.bottom, margin.top]);

        // Y2: exits for line
        const y2 = d3
            .scaleLinear()
            .domain([0, d3.max(exitData, (d) => d.exits) * 1.1])
            .nice()
            .range([height - margin.bottom, margin.top]);

        // Color scale for different values
        const color = d3
            .scaleSequential()
            .domain([0, maxValuesPerMonth - 1])
            .interpolator(d3.interpolateViridis);

        // X Axis
        const xAxis = d3
            .select(svgRef.current)
            .append('g')
            .attr('transform', `translate(0,${height - margin.bottom})`)
            .call(d3.axisBottom(x0));
        xAxis.selectAll('path').attr('stroke', '#e3e3e3');
        xAxis.selectAll('.tick line').attr('stroke', 'none');
        xAxis.selectAll('.tick text').attr('fill', '#787878').style('font-size', '14px');

        // Y Axis (left) for percentages
        const yAxis = d3
            .select(svgRef.current)
            .append('g')
            .attr('transform', `translate(${margin.left},0)`)
            .call(
                d3
                    .axisLeft(y)
                    .ticks(5)
                    .tickFormat((d) => `${d}%`)
            );
        yAxis.selectAll('path').attr('stroke', 'none');
        yAxis.selectAll('.tick line').attr('stroke', 'none');
        yAxis.selectAll('.tick text').attr('fill', '#e3e3e3').style('font-size', '14px');

        // Add Y-axis label
        yAxis
            .append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - margin.left + 10) // Adjust position as needed
            .attr('x', 0 - height / 2) // Adjust position as needed
            .attr('dy', '1em')
            .style('text-anchor', 'middle')
            .style('font-size', '14px')
            .attr('fill', '#e3e3e3')
            .text('% of Annual Rent or Equivalent');

        // Y Axis (right) for exits
        const y2Domain = y2.domain(); // Get the "niced" domain from the y2 scale
        const desiredTickCount = 5; // We want exactly 5 ticks

        // Calculate the values for these 5 ticks
        const customY2TickValues = d3
            .range(desiredTickCount)
            .map((i) => y2Domain[0] + (i * (y2Domain[1] - y2Domain[0])) / (desiredTickCount - 1));

        const yAxis2 = d3
            .select(svgRef.current)
            .append('g')
            .attr('transform', `translate(${width - margin.right},0)`)
            .call(d3.axisRight(y2).tickValues(customY2TickValues)); // Use the explicit tick values
        yAxis2.selectAll('path').attr('stroke', 'none');
        yAxis2.selectAll('.tick line').attr('stroke', 'none');
        yAxis2.selectAll('.tick text').attr('fill', '#e3e3e3').style('font-size', '14px');

        yAxis2
            .append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', margin.right - 40) // Adjust position as needed
            .attr('x', 0 - height / 2) // Adjust position as needed
            .attr('dy', '1em')
            .style('text-anchor', 'middle')
            .style('font-size', '14px')
            .attr('fill', '#e3e3e3')
            .text('Number of pending/signed leases');

        // Dynamic bar width calculation
        const minBarWidth = 3;
        const maxBarWidth = 15;
        const barSpacing = 2;

        // Function to calculate bar width based on number of bars
        const calculateBarWidth = (numBars) => {
            const availableWidth = x0.bandwidth() - (numBars - 1) * barSpacing;
            const calculatedWidth = availableWidth / (numBars + 1);
            return Math.min(Math.max(calculatedWidth, minBarWidth), maxBarWidth);
        };

        // Add this once to your SVG (before drawing bars)
        const svg = d3.select(svgRef.current);
        svg.append('defs').append('filter').attr('id', 'barShadow').html(`
                <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#000" flood-opacity="0.08"/>
            `);

        // Bars
        svg.append('g')
            .selectAll('g')
            .data(processed)
            .join('g')
            .attr('transform', (d) => {
                const n = d.values.length;
                const barWidth = calculateBarWidth(n);
                const groupWidth = n * barWidth + (n - 1) * barSpacing;
                const offset = (x0.bandwidth() - groupWidth) / 2;
                return `translate(${x0(d.month) + offset},0)`;
            })
            .selectAll('rect')
            .data((d) => d.values.map((v, index) => ({...v, index})))
            .join('rect')
            .attr('x', (d, i, nodes) => {
                const parent = d3.select(nodes[i].parentNode);
                const numBars = parent.datum().values.length;
                const barWidth = calculateBarWidth(numBars);
                return i * (barWidth + barSpacing);
            })
            .attr('y', (d) => y(d.value))
            .attr('width', (d, i, nodes) => {
                const parent = d3.select(nodes[i].parentNode);
                const numBars = parent.datum().values.length;
                return calculateBarWidth(numBars);
            })
            .attr('height', (d) => y(0) - y(d.value))
            .attr('fill', (d) => d.color || color(d.index))
            .attr('filter', 'url(#barShadow)')
            .on('mouseover', (event, d) => {
                const tooltip = tooltipRef.current;
                tooltip.style.display = 'block';
                tooltip.innerHTML = `<div><strong>Details:</strong> ${d.text}</div><div><strong>Estimated % of Annual Rent:</strong> ${d.value}%</div>`;
                const containerRect = containerRef.current.getBoundingClientRect();
                const tooltipRect = tooltip.getBoundingClientRect();
                const mouseX = event.clientX - containerRect.left;
                const mouseY = event.clientY - containerRect.top;

                // Calculate available space on right and left
                const spaceOnRight = containerRect.width - mouseX;
                const spaceOnLeft = mouseX;

                // Position tooltip based on available space
                if (spaceOnRight >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX + 10 + 'px';
                } else if (spaceOnLeft >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX - tooltipRect.width - 10 + 'px';
                } else {
                    // If no space on either side, position at the edge
                    tooltip.style.left = spaceOnRight > spaceOnLeft ? containerRect.width - tooltipRect.width - 10 + 'px' : 10 + 'px';
                }
                tooltip.style.top = mouseY - 10 + 'px';
            })
            .on('mousemove', (event) => {
                const tooltip = tooltipRef.current;
                const containerRect = containerRef.current.getBoundingClientRect();
                const tooltipRect = tooltip.getBoundingClientRect();
                const mouseX = event.clientX - containerRect.left;
                const mouseY = event.clientY - containerRect.top;

                // Calculate available space on right and left
                const spaceOnRight = containerRect.width - mouseX;
                const spaceOnLeft = mouseX;

                // Position tooltip based on available space
                if (spaceOnRight >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX + 10 + 'px';
                } else if (spaceOnLeft >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX - tooltipRect.width - 10 + 'px';
                } else {
                    // If no space on either side, position at the edge
                    tooltip.style.left = spaceOnRight > spaceOnLeft ? containerRect.width - tooltipRect.width - 10 + 'px' : 10 + 'px';
                }
                tooltip.style.top = mouseY - 10 + 'px';
            })
            .on('mouseout', () => {
                const tooltip = tooltipRef.current;
                tooltip.style.display = 'none';
            });

        // Line for exits
        const line = d3
            .line()
            .x((d) => x0(d.month) + x0.bandwidth() / 2)
            .y((d) => y2(d.exits));

        // Add the line path
        svg.append('path').datum(exitData).attr('fill', 'none').attr('stroke', '#FF6B6B').attr('stroke-width', 2).attr('d', line);

        // Add dots for exits
        svg.selectAll('.exit-dot')
            .data(exitData)
            .join('circle')
            .attr('class', 'exit-dot')
            .attr('cx', (d) => x0(d.month) + x0.bandwidth() / 2)
            .attr('cy', (d) => y2(d.exits))
            .attr('r', 4)
            .attr('fill', '#FF6B6B')
            .attr('stroke', 'none')
            .attr('stroke-width', 2)
            .on('mouseover', (event, d) => {
                const tooltip = tooltipRef.current;
                tooltip.style.display = 'block';
                tooltip.innerHTML = `<div><strong>Month:</strong> ${d.month}</div><div><strong>Pending/signed leases:</strong> ${d.exits}</div>`;
                const containerRect = containerRef.current.getBoundingClientRect();
                const tooltipRect = tooltip.getBoundingClientRect();
                const mouseX = event.clientX - containerRect.left;
                const mouseY = event.clientY - containerRect.top;

                // Calculate available space on right and left
                const spaceOnRight = containerRect.width - mouseX;
                const spaceOnLeft = mouseX;

                // Position tooltip based on available space
                if (spaceOnRight >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX + 10 + 'px';
                } else if (spaceOnLeft >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX - tooltipRect.width - 10 + 'px';
                } else {
                    // If no space on either side, position at the edge
                    tooltip.style.left = spaceOnRight > spaceOnLeft ? containerRect.width - tooltipRect.width - 10 + 'px' : 10 + 'px';
                }
                tooltip.style.top = mouseY - 10 + 'px';
            })
            .on('mousemove', (event) => {
                const tooltip = tooltipRef.current;
                const containerRect = containerRef.current.getBoundingClientRect();
                const tooltipRect = tooltip.getBoundingClientRect();
                const mouseX = event.clientX - containerRect.left;
                const mouseY = event.clientY - containerRect.top;

                // Calculate available space on right and left
                const spaceOnRight = containerRect.width - mouseX;
                const spaceOnLeft = mouseX;

                // Position tooltip based on available space
                if (spaceOnRight >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX + 10 + 'px';
                } else if (spaceOnLeft >= tooltipRect.width + 10) {
                    tooltip.style.left = mouseX - tooltipRect.width - 10 + 'px';
                } else {
                    // If no space on either side, position at the edge
                    tooltip.style.left = spaceOnRight > spaceOnLeft ? containerRect.width - tooltipRect.width - 10 + 'px' : 10 + 'px';
                }
                tooltip.style.top = mouseY - 10 + 'px';
            })
            .on('mouseout', () => {
                const tooltip = tooltipRef.current;
                tooltip.style.display = 'none';
            });
    }, [data, exitData, width, height]);

    return (
        <div ref={containerRef} style={{width: '100%', position: 'relative'}}>
            <svg ref={svgRef} width={width} height={height}></svg>
            <div
                ref={tooltipRef}
                style={{
                    position: 'absolute',
                    pointerEvents: 'none',
                    background: 'white',
                    border: '1px solid #e3e3e3',
                    borderRadius: 8,
                    padding: '10px 14px',
                    fontSize: 14,
                    color: '#222',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    zIndex: 10,
                    display: 'none',
                    maxWidth: 400,
                    width: 400,
                    whiteSpace: 'pre-line',
                    lineHeight: 1.5
                }}
            />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 24,
                    paddingBottom: '24px'
                }}
            >
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <span
                        style={{
                            width: 12,
                            height: 12,
                            background: '#FEE3A0',
                            borderRadius: 4,
                            display: 'inline-block',
                            border: 'none'
                        }}
                    ></span>
                    <span style={{fontSize: 14, color: '#222'}}>Traditional concessions</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <span
                        style={{
                            width: 12,
                            height: 12,
                            background: '#149CE0',
                            borderRadius: 4,
                            display: 'inline-block',
                            border: 'none'
                        }}
                    ></span>
                    <span style={{fontSize: 14, color: '#222'}}>Stake+ Offers</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <span
                        style={{
                            width: 12,
                            height: 2,
                            background: '#FF6B6B',
                            borderRadius: 4,
                            display: 'inline-block',
                            border: 'none'
                        }}
                    ></span>
                    <span style={{fontSize: 14, color: '#222'}}>Pending/signed leases</span>
                </div>
            </div>
        </div>
    );
};

export default D3GroupedBarChart;
